import React, { useState, useEffect } from 'react';
import Select from 'react-select';

// Компонент для отображения подкатегории
const SubcategoryMessages = ({ subcat, messages, categories, handleChangeMessageCategory, getCategoryFullName }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Создаем опции для react-select
  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.parent_id ? 
      `${getCategoryFullName(cat.id)}` : 
      cat.name
  }));
  
  return (
    <div className="ml-6 mt-4 mb-4 border-l-2 border-gray-200 pl-4">
      <div className="flex items-center mb-2">
        <button 
          className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? "Expand" : "Collapse"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {isCollapsed 
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /> 
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            }
          </svg>
        </button>
        <h4 className="text-md font-medium">
          {subcat.name}
          <span className="text-sm text-gray-500 ml-2">
            ({messages.length} messages)
          </span>
        </h4>
      </div>
      
      {!isCollapsed && (
        <>
          {messages.length > 0 ? (
            messages.map((message) => (
              <div key={message.id} className="p-3 border mb-2 rounded-md">
                <div className="text-sm mb-2 break-words">
                    {message.message}
                    {message.error_code && (
                        <span className="ml-2 px-2 py-1 bg-gray-200 text-gray-800 text-xs rounded">
                        Status: {message.error_code}
                        </span>
                    )}
                </div>
                <div className="flex justify-between items-center">
                  <Select
                    className="w-full"
                    options={categoryOptions}
                    value={categoryOptions.find(opt => opt.value === message.category_id)}
                    onChange={(selectedOption) => handleChangeMessageCategory(message.id, selectedOption.value)}
                    isSearchable
                    placeholder="Select category..."
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              No messages in this subcategory
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Компонент для отображения категории и ее подкатегорий
const CategoryMessages = ({ category, directMessages, allCategoryMessages, messagesByCategory, categories, subcategories, handleChangeMessageCategory, getCategoryFullName }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Создаем опции для react-select
  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.parent_id ? 
      `${getCategoryFullName(cat.id)}` : 
      cat.name
  }));
  
  return (
    <div className="mb-4">
      <div className="flex items-center mb-2">
        <button 
          className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? "Expand" : "Collapse"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {isCollapsed 
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /> 
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            }
          </svg>
        </button>
        <h3 className="text-lg font-medium">
          {category.name} 
          <span className="text-sm text-gray-500 ml-2">
            ({directMessages.length} direct, {allCategoryMessages.length} total)
          </span>
        </h3>
      </div>
      
      {!isCollapsed && (
        <>
          {directMessages.length > 0 ? (
            directMessages.map((message) => (
              <div key={message.id} className="p-3 border mb-2 rounded-md">
                <div className="text-sm mb-2 break-words">
                    {message.message}
                    {message.error_code && (
                        <span className="ml-2 px-2 py-1 bg-gray-200 text-gray-800 text-xs rounded">
                        Status: {message.error_code}
                        </span>
                    )}
                </div>
                <div className="flex justify-between items-center">
                  <Select
                    className="w-full"
                    options={categoryOptions}
                    value={categoryOptions.find(opt => opt.value === message.category_id)}
                    onChange={(selectedOption) => handleChangeMessageCategory(message.id, selectedOption.value)}
                    isSearchable
                    placeholder="Select category..."
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              No messages in this category
            </div>
          )}
          
          {/* Показываем сообщения в подкатегориях */}
          {subcategories.map(subcat => (
            <SubcategoryMessages
              key={subcat.id}
              subcat={subcat}
              messages={messagesByCategory[subcat.id] || []}
              categories={categories}
              handleChangeMessageCategory={handleChangeMessageCategory}
              getCategoryFullName={getCategoryFullName}
            />
          ))}
        </>
      )}
    </div>
  );
};

// Компонент для отображения отдельной категории в левой панели
const CategoryItem = ({ category, categories, onEdit, onDelete, messagesByCategory }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const subcategories = categories.filter(cat => cat.parent_id === category.id);
  
  // Вычисляем общее количество сообщений для этой категории и всех ее подкатегорий
  const getSubcategoryIds = (parentId) => {
    const ids = [parentId];
    categories
      .filter(cat => cat.parent_id === parentId)
      .forEach(subcat => {
        ids.push(...getSubcategoryIds(subcat.id));
      });
    return ids;
  };
  
  const allCategoryIds = getSubcategoryIds(category.id);
  const directMessages = messagesByCategory[category.id]?.length || 0;
  const totalMessages = allCategoryIds.reduce((total, id) => 
    total + (messagesByCategory[id]?.length || 0), 0);
  
  return (
    <div className="mb-2">
      <div className="p-3 border rounded-md flex justify-between items-center">
        <div className="flex-1">
          <div className="flex items-center">
            {subcategories.length > 0 && (
              <button 
                className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                onClick={() => setIsCollapsed(!isCollapsed)}
                aria-label={isCollapsed ? "Expand" : "Collapse"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isCollapsed 
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /> 
                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  }
                </svg>
              </button>
            )}
            <div className="font-medium">{category.name}</div>
          </div>
          {category.description && (
            <div className="text-sm text-gray-500">{category.description}</div>
          )}
          <div className="text-xs text-gray-400">
            {subcategories.length > 0 
              ? `(${directMessages} direct, ${totalMessages} total)`
              : `${directMessages} messages`
            }
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            className={`p-1 text-blue-600 hover:text-blue-800 ${category.is_default ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !category.is_default && onEdit(category)}
            disabled={category.is_default}
          >
            Edit
          </button>
          <button
            className={`p-1 text-red-600 hover:text-red-800 ${category.is_default ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !category.is_default && onDelete(category.id)}
            disabled={category.is_default}
          >
            Delete
          </button>
        </div>
      </div>
      
      {subcategories.length > 0 && !isCollapsed && (
        <div className="ml-6 mt-2 border-l-2 border-gray-200 pl-4">
          {subcategories.map(subcat => (
            <CategoryItem 
              key={subcat.id}
              category={subcat}
              categories={categories}
              onEdit={onEdit}
              onDelete={onDelete}
              messagesByCategory={messagesByCategory}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Основной компонент управления категориями ошибок
const ErrorCategoriesManager = (props) => {
  const [categories, setCategories] = useState(props.error_categories || []);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [filterText, setFilterText] = useState('');

  // Исправленная функция для уведомлений
  const showNotification = (message, type = 'info') => {
    // Создаем новый элемент div для уведомления
    const notificationDiv = document.createElement('div');
    notificationDiv.className = `notification ${type}`;
    notificationDiv.textContent = message;
    notificationDiv.style.position = 'fixed';
    notificationDiv.style.top = '20px';
    notificationDiv.style.right = '20px';
    notificationDiv.style.padding = '10px 20px';
    notificationDiv.style.borderRadius = '4px';
    notificationDiv.style.zIndex = '1000';
    notificationDiv.style.transition = 'opacity 0.5s';
    
    // Устанавливаем цвет фона в зависимости от типа уведомления
    if (type === 'success') {
      notificationDiv.style.background = '#4caf50';
      notificationDiv.style.color = 'white';
    } else if (type === 'error') {
      notificationDiv.style.background = '#f44336';
      notificationDiv.style.color = 'white';
    } else {
      notificationDiv.style.background = '#2196f3';
      notificationDiv.style.color = 'white';
    }
    
    // Добавляем элемент в body
    document.body.appendChild(notificationDiv);
    
    // Используем идентификатор таймера для корректного удаления элемента
    setTimeout(() => {
      notificationDiv.style.opacity = '0';
      
      // Создаем еще один таймер для удаления элемента после анимации
      setTimeout(() => {
        // Проверяем, существует ли элемент перед его удалением
        if (document.body.contains(notificationDiv)) {
          document.body.removeChild(notificationDiv);
        }
      }, 500);
    }, 3000);
  };

  // Загрузка сообщений
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await fetch('/api/error_messages');
        const data = await response.json();
        setMessages(data.error_messages || []);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching messages:', error);
        showNotification('Failed to load error messages', 'error');
        setLoading(false);
      }
    };
    
    fetchMessages();
  }, []);
  
  // Получаем полное имя категории (включая родителей)
  const getCategoryFullName = (categoryId) => {
    const getFullNameRecursive = (catId, path = []) => {
      const category = categories.find(c => c.id === catId);
      if (!category) return path;
      
      path.unshift(category.name);
      
      if (category.parent_id) {
        return getFullNameRecursive(category.parent_id, path);
      }
      
      return path;
    };
    
    return getFullNameRecursive(categoryId).join(' > ');
  };
  
  // Создание новой категории
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    
    if (!editingCategory.name || editingCategory.name.trim() === '') {
      showNotification('Category name cannot be empty', 'error');
      return;
    }
    
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch('/error_categories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify({ error_category: editingCategory }),
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        setCategories([...categories, responseData.error_category]);
        setEditingCategory(null);
        showNotification('Category created successfully', 'success');
      } else {
        showNotification(responseData.errors?.join(', ') || 'Failed to create category', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('An error occurred', 'error');
    }
  };
  
  // Обновление категории
  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    
    if (!editingCategory.name || editingCategory.name.trim() === '') {
      showNotification('Category name cannot be empty', 'error');
      return;
    }
    
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch(`/error_categories/${editingCategory.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify({ error_category: editingCategory }),
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        setCategories(categories.map(cat => 
          cat.id === editingCategory.id ? responseData.error_category : cat
        ));
        setEditingCategory(null);
        showNotification('Category updated successfully', 'success');
      } else {
        showNotification(responseData.errors?.join(', ') || 'Failed to update category', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('An error occurred', 'error');
    }
  };
  
  // Удаление категории
  const handleDeleteCategory = async (id) => {
    if (!confirm('Are you sure you want to delete this category? All messages will be moved to Unsorted.')) {
      return;
    }
    
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch(`/error_categories/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': token,
        },
      });
      
      if (response.ok) {
        setCategories(categories.filter(cat => cat.id !== id));
        showNotification('Category deleted successfully', 'success');
      } else {
        const error = await response.json();
        showNotification(error.errors?.join(', ') || 'Failed to delete category', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('An error occurred', 'error');
    }
  };
  
  // Обновление категории для сообщения
  const handleChangeMessageCategory = async (messageId, categoryId) => {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      
      const response = await fetch(`/api/error_messages/${messageId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify({ 
          error_message: { error_category_id: categoryId } 
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(messages.map(msg => 
          msg.id === messageId ? data.error_message : msg
        ));
        showNotification('Message category updated', 'success');
      } else {
        showNotification('Failed to update message category', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('An error occurred', 'error');
    }
  };
  
  // Проверка возможности выбора категории как родительской (избегаем циклов)
  const canBeParent = (categoryId, potentialParentId) => {
    if (categoryId === potentialParentId) return false;
    
    // Проверяем всю цепочку родителей
    const checkParentChain = (parentId) => {
      if (!parentId) return true;
      if (parentId === categoryId) return false;
      
      const parent = categories.find(c => c.id === parentId);
      return parent ? checkParentChain(parent.parent_id) : true;
    };
    
    return checkParentChain(potentialParentId);
  };

  // Фильтрация сообщений
  const filteredMessages = messages.filter(message => 
    message.message.toLowerCase().includes(filterText.toLowerCase())
  );

  // Группировка сообщений по категориям
  const messagesByCategory = {};
  categories.forEach(cat => {
    messagesByCategory[cat.id] = filteredMessages.filter(msg => msg.category_id === cat.id);
  });
  
  // Находим категорию "Unsorted"
  const unsortedCategory = categories.find(cat => cat.is_default);
  const unsortedCategoryId = unsortedCategory?.id;

  if (loading) {
    return <div className="flex justify-center items-center p-5">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Error Categories Management</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Категории */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Categories</h2>
            {!editingCategory && (
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => setEditingCategory({ name: '', description: '', parent_id: null })}
              >
                Add New
              </button>
            )}
          </div>
          
          {editingCategory && (
            <form onSubmit={editingCategory.id ? handleUpdateCategory : handleCreateCategory} className="mb-6 p-4 border rounded-md">
              <h3 className="text-lg font-medium mb-3">
                {editingCategory.id ? 'Edit Category' : 'New Category'}
              </h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  className="w-full px-3 py-2 border rounded-md"
                  value={editingCategory.description || ''}
                  onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
                <Select
                  className="w-full"
                  options={[
                    { value: '', label: 'None (Root Category)' },
                    ...categories
                      .filter(category => 
                        !category.is_default && 
                        (!editingCategory.id || canBeParent(editingCategory.id, category.id))
                      )
                      .map(category => ({
                        value: category.id,
                        label: getCategoryFullName(category.id),
                        isDisabled: category.is_default
                      }))
                  ]}
                  value={
                    editingCategory.parent_id 
                      ? { 
                          value: editingCategory.parent_id,
                          label: getCategoryFullName(editingCategory.parent_id)
                        }
                      : { value: '', label: 'None (Root Category)' }
                  }
                  onChange={(selectedOption) => setEditingCategory({
                    ...editingCategory, 
                    parent_id: selectedOption.value ? parseInt(selectedOption.value) : null
                  })}
                  isSearchable
                  placeholder="Select parent category..."
                />
              </div>
              
              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingCategory.id ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  onClick={() => setEditingCategory(null)}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          
          <div className="space-y-2">
            {/* Отображаем только корневые категории */}
            {categories
              .filter(cat => !cat.parent_id) // только корневые категории
              .map((category) => (
                <CategoryItem 
                  key={category.id}
                  category={category}
                  categories={categories}
                  onEdit={setEditingCategory}
                  onDelete={handleDeleteCategory}
                  messagesByCategory={messagesByCategory}
                />
              ))}
          </div>
        </div>
        
        {/* Сообщения об ошибках */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Error Messages</h2>
          </div>
          
          {/* Фильтрация */}
          <div className="mb-4">
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-md"
              placeholder="Filter messages..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
          
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {/* Показываем сначала несортированные сообщения */}
            <div className="mb-4">
              <h3 className="text-lg font-medium mb-2">Unsorted Messages</h3>
              {messagesByCategory[unsortedCategoryId]?.length > 0 ? (
                messagesByCategory[unsortedCategoryId].map((message) => (
                  <div key={message.id} className="p-3 border mb-2 rounded-md bg-yellow-50">
                    <div className="text-sm mb-2 break-words">
                        {message.message}
                        {message.error_code && (
                            <span className="ml-2 px-2 py-1 bg-gray-200 text-gray-800 text-xs rounded">
                            Status: {message.error_code}
                            </span>
                        )}
                    </div>
                    <div className="flex justify-between items-center">
                      <Select
                        className="w-full"
                        options={categories.map(cat => ({
                          value: cat.id,
                          label: cat.parent_id ? 
                            `${getCategoryFullName(cat.id)}` : 
                            cat.name
                        }))}
                        value={{
                          value: message.category_id,
                          label: message.category_id ? 
                            (message.category_parent_id ? 
                              `${getCategoryFullName(message.category_id)}` : 
                              categories.find(c => c.id === message.category_id)?.name || "Unsorted") : 
                            "Unsorted"
                        }}
                        onChange={(selectedOption) => handleChangeMessageCategory(message.id, selectedOption.value)}
                        isSearchable
                        placeholder="Select category..."
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  No unsorted messages
                </div>
              )}
            </div>
            
            {/* Остальные категории */}
            {categories
              .filter(cat => !cat.is_default && !cat.parent_id)
              .map((category) => {
                // Получаем все сообщения для этой категории и всех её подкатегорий
                const categoryIds = [category.id];
                const getSubcategoryIds = (parentId) => {
                  categories
                    .filter(cat => cat.parent_id === parentId)
                    .forEach(subcat => {
                      categoryIds.push(subcat.id);
                      getSubcategoryIds(subcat.id);
                    });
                };
                getSubcategoryIds(category.id);
                
                const allCategoryMessages = filteredMessages.filter(msg => 
                  categoryIds.includes(msg.category_id)
                );
                
                // Сообщения только этой категории (не включая подкатегории)
                const directCategoryMessages = messagesByCategory[category.id] || [];
                
                // Подкатегории для этой категории
                const subcats = categories.filter(cat => cat.parent_id === category.id);
                
                return (
                  <CategoryMessages
                    key={category.id}
                    category={category}
                    directMessages={directCategoryMessages}
                    allCategoryMessages={allCategoryMessages}
                    messagesByCategory={messagesByCategory}
                    categories={categories}
                    subcategories={subcats}
                    handleChangeMessageCategory={handleChangeMessageCategory}
                    getCategoryFullName={getCategoryFullName}
                  />
                );
              })}
            
            {filteredMessages.length === 0 && (
              <div className="text-center text-gray-500 my-8">
                No error messages found matching your filter
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorCategoriesManager;